import { useState, useMemo } from "react";
import { KB_CATEGORIES } from "@/content/kb";
import { PRODUCT_UPDATES } from "@/content/updates";
import { Input } from "@/components/ui/input";
import {
  Search, ChevronRight, ArrowLeft, Rocket, LayoutGrid, ShoppingCart,
  Package, Users, Mail, Calendar, Settings as SettingsIcon, HelpCircle, CheckCircle2, Sparkles, MapPin,
} from "lucide-react";

const ICONS = { Rocket, LayoutGrid, ShoppingCart, Package, Users, Mail, Calendar, Settings: SettingsIcon, MapPin };

const TAG_STYLES = {
  New: "bg-primary/10 text-primary border-primary/20",
  Improvement: "bg-amber-100 text-amber-700 border-amber-200",
  Fix: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function WhatsNew() {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-8 border border-border bg-card" data-testid="help-whats-new">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/40 text-left"
        data-testid="help-whats-new-toggle"
      >
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="font-display font-bold text-sm uppercase tracking-wider">What&rsquo;s new</span>
        <span className="text-[11px] text-muted-foreground ml-1">Product updates from the MorrCart team</span>
        <ChevronRight className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="divide-y divide-border">
          {PRODUCT_UPDATES.map((u, i) => (
            <div key={i} className="flex gap-3 p-4" data-testid={`help-update-${i}`}>
              <img src="/morrmark.png" alt="MorrCart" onError={(e) => { e.currentTarget.style.display = "none"; }}
                   className="h-9 w-9 rounded-full border border-border bg-white object-contain p-1 flex-none" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">MorrCart Product Updates</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${TAG_STYLES[u.tag] || TAG_STYLES.New}`}>{u.tag}</span>
                  <span className="text-xs text-muted-foreground">{u.date}</span>
                </div>
                <div className="font-medium text-sm mt-1">{u.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{u.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KbImage({ src, alt }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setOk(false)}
      className="mt-3 w-full max-w-2xl rounded-md border border-border shadow-sm"
      data-testid="kb-article-image"
    />
  );
}

export function HelpCenter() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(KB_CATEGORIES[0].id);
  const [openArticle, setOpenArticle] = useState(null); // {catId, artId}

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const out = [];
    KB_CATEGORIES.forEach((c) => {
      c.articles.forEach((a) => {
        const hay = (a.title + " " + a.why + " " + a.steps.join(" ")).toLowerCase();
        if (hay.includes(q)) out.push({ cat: c, art: a });
      });
    });
    return out;
  }, [query]);

  const cat = KB_CATEGORIES.find((c) => c.id === activeCat) || KB_CATEGORIES[0];
  const article = openArticle
    ? KB_CATEGORIES.find((c) => c.id === openArticle.catId)?.articles.find((a) => a.id === openArticle.artId)
    : null;

  const openArt = (catId, artId) => { setOpenArticle({ catId, artId }); setQuery(""); };

  return (
    <div data-testid="help-center">
      <div className="mb-6">
        <h2 className="font-display font-extrabold tracking-tight text-2xl flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-primary" /> Help Center
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Plain-English guides for everything in your dashboard.</p>
        <div className="relative mt-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="rounded-none pl-9"
            placeholder="Search help (e.g. tax, PIN, pause orders)…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpenArticle(null); }}
            data-testid="help-search-input"
          />
        </div>
      </div>

      {/* Search results */}
      {results ? (
        <div data-testid="help-search-results">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
            {results.length} result{results.length === 1 ? "" : "s"}
          </p>
          <div className="space-y-2 max-w-2xl">
            {results.map(({ cat: c, art: a }) => (
              <button
                key={c.id + a.id}
                onClick={() => openArt(c.id, a.id)}
                className="w-full text-left border border-border bg-card p-3 hover:border-primary transition-colors flex items-center justify-between group"
                data-testid={`help-result-${a.id}`}
              >
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.title}</div>
                  <div className="font-medium">{a.title}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
              </button>
            ))}
            {!results.length && <p className="text-sm text-muted-foreground">No matches. Try a different word.</p>}
          </div>
        </div>
      ) : article ? (
        /* Single article view */
        <div className="max-w-2xl" data-testid="help-article">
          <button onClick={() => setOpenArticle(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4" data-testid="help-article-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h3 className="font-display font-bold text-xl">{article.title}</h3>
          <div className="mt-2 flex items-start gap-2 text-sm bg-primary/5 border-l-2 border-primary p-3">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-none" />
            <span><span className="font-semibold">Why it matters:</span> {article.why}</span>
          </div>
          <ol className="mt-4 space-y-3">
            {article.steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`help-step-${i}`}>
                <span className="flex-none h-6 w-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-sm leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
          {(article.images || []).map((img, i) => <KbImage key={i} src={img.src} alt={img.alt} />)}
        </div>
      ) : (
        /* Category browse */
        <div>
          <WhatsNew />
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          <div className="lg:border-r lg:border-border lg:pr-4">
            <div className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
              {KB_CATEGORIES.map((c) => {
                const Icon = ICONS[c.icon] || HelpCircle;
                const active = c.id === activeCat;
                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveCat(c.id)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm text-left whitespace-nowrap transition-colors ${active ? "bg-primary text-white" : "hover:bg-secondary"}`}
                    data-testid={`help-cat-${c.id}`}
                  >
                    <Icon className="h-4 w-4 flex-none" /> {c.title}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="font-display font-bold text-lg">{cat.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{cat.blurb}</p>
            <div className="space-y-2">
              {cat.articles.map((a) => (
                <button
                  key={a.id}
                  onClick={() => openArt(cat.id, a.id)}
                  className="w-full text-left border border-border bg-card p-3 hover:border-primary transition-colors flex items-center justify-between group"
                  data-testid={`help-article-link-${a.id}`}
                >
                  <div>
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.why}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-none ml-2" />
                </button>
              ))}
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
